import { CheckCircle2, Clock, Filter, Settings, Users, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import AppCard from "../../components/AppCard";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import { useDemo } from "../../providers/DemoProvider";
import { formatDateTime, getScopeLabel } from "../../services/demoSelectors";
import type { AnalysisScope, PlatformType } from "../../types/demo";

const platformLabel = (platform: PlatformType): string => {
  const labels: Record<PlatformType, string> = {
    xero: "Xero",
    myob: "MYOB",
    quickbooks: "QuickBooks",
    sage: "Sage",
    hubspot: "HubSpot",
    stripe: "Stripe",
    procore: "Procore",
    aroflo: "AroFlo",
  };

  return labels[platform];
};

export default () => {
  const { state, saveReport, deleteReport } = useDemo();
  const [editingId, setEditingId] = useState<string | null>(null);
  const groups = [...state.groups].sort((a, b) => a.name.localeCompare(b.name));
  const ungroupedConnections = state.connections.filter(connection => connection.groupIds.length === 0);
  const form = useForm<{ name: string; query: string; scheduleText: string; scope: AnalysisScope; recipients: string; subject: string; active: boolean }>({
    defaultValues: {
      name: "",
      query: state.pendingPrompt || "",
      scheduleText: "Every Monday at 9am",
      scope: "all",
      recipients: "owner@acme.com, finance@acme.com",
      subject: "Intelligence Brief: Weekly Financials",
      active: true,
    },
  });

  const onSubmit = (values: { name: string; query: string; scheduleText: string; scope: AnalysisScope; recipients: string; subject: string; active: boolean }) => {
    saveReport({
      id: editingId ?? undefined,
      name: values.name,
      query: values.query,
      scheduleText: values.scheduleText,
      scope: values.scope,
      recipients: values.recipients.split(",").map(item => item.trim()).filter(Boolean),
      subject: values.subject,
      active: values.active,
    });
    setEditingId(null);
    form.reset({ ...values, name: "", query: "", recipients: "owner@acme.com" });
  };

  return (
    <div className="space-y-6">
      <AppCard title={editingId ? "Modify Automated Analysis" : "Automate Insights"} subtitle="Recurring AI-driven performance briefs">
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-1.5">
            <label className="ml-1 text-xs font-bold tracking-wider text-slate-400">Briefing Name</label>
            <input {...form.register("name", { required: true })} placeholder="e.g., Executive Monday Review" className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-indigo-500" />
          </div>
          <div className="space-y-1.5">
            <label className="ml-1 text-xs font-bold tracking-wider text-slate-400">Email Subject Line</label>
            <input {...form.register("subject", { required: true })} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-indigo-500" />
          </div>
          <div className="space-y-1.5 lg:col-span-2">
            <label className="ml-1 text-xs font-bold tracking-wider text-slate-400">Intelligence Query</label>
            <textarea {...form.register("query", { required: true })} className="min-h-[100px] w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-indigo-500" />
          </div>
          <div className="space-y-1.5">
            <label className="ml-1 text-xs font-bold tracking-wider text-slate-400">Execution Frequency</label>
            <div className="relative">
              <input {...form.register("scheduleText", { required: true })} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 pl-10 text-sm outline-none focus:border-indigo-500" />
              <Clock className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="ml-1 text-xs font-bold tracking-wider text-slate-400">Analysis Scope</label>
            <select {...form.register("scope")} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-indigo-500">
              <option value="all">All Connections</option>
              {groups.length > 0 && <option value="__report-group-header" disabled>--- Groups ---</option>}
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
              {ungroupedConnections.length > 0 && <option value="__report-connection-header" disabled>--- Connections ---</option>}
              {ungroupedConnections.map(connection => (
                <option key={connection.id} value={connection.id}>{connection.name} ({platformLabel(connection.platform)})</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5 lg:col-span-2">
            <label className="ml-1 text-xs font-bold tracking-wider text-slate-400">Recipients (Comma separated)</label>
            <div className="relative">
              <input {...form.register("recipients")} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 pl-10 text-sm outline-none focus:border-indigo-500" />
              <Users className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
            </div>
          </div>
          <div className="flex items-center gap-6 pt-2 lg:col-span-2">
            <label className="flex items-center gap-2 text-sm font-bold text-slate-500">
              <input type="checkbox" {...form.register("active")} className="h-4 w-4 rounded border-slate-200 bg-slate-50 text-indigo-600 outline-none ring-0" />
              Status: Active
            </label>
            <div className="flex-1 text-right">
              <Button type="submit" className="px-8 text-xs font-semibold shadow-sm">{editingId ? "Save Changes" : "Deploy Briefing"}</Button>
            </div>
          </div>
        </form>
      </AppCard>

      <AppCard title="Active Automations" subtitle="Recurring intelligence briefs">
        <div className="grid gap-4 md:grid-cols-2">
          {state.scheduledReports.length === 0 && <div className="col-span-2 rounded-xl border border-dashed border-slate-200 p-8 text-center font-medium text-slate-400">No active briefs.</div>}
          {state.scheduledReports.map(report => (
            <div key={report.id} className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/50 p-6 transition-all hover:border-slate-200 hover:bg-white hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-slate-800">{report.name}</h4>
                    <Badge tone={report.active ? "success" : "neutral"} label={report.active ? "LIVE" : "PAUSED"} size="sm" />
                  </div>
                  <p className="line-clamp-2 text-xs font-medium text-slate-500">{report.query}</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400"><Filter className="h-3 w-3" /> {getScopeLabel(state, report.scope)}</div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400"><Clock className="h-3 w-3" /> {report.scheduleText}</div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Next Run: {formatDateTime(report.nextRunAt)}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => {
                    setEditingId(report.id);
                    form.reset({ name: report.name, query: report.query, scheduleText: report.scheduleText, scope: report.scope, recipients: report.recipients.join(", "), subject: report.subject, active: report.active });
                  }}><Settings className="h-4 w-4" /></Button>
                  <Button variant="danger" size="sm" className="h-8 w-8 p-0" onClick={() => deleteReport(report.id)}><X className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </AppCard>
    </div>
  );
};
